import { ChangeDetectionStrategy, Component } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { toZonedTime } from 'date-fns-tz';
import { RktButtonDirective } from '@rocketfinancialcorp/rocket-ui/button';
import { RktFormComponent, formDatepicker, formInput, formRow } from '@rocketfinancialcorp/rocket-ui/form';
import { RktIconComponent } from '@rocketfinancialcorp/rocket-ui/icon';
import { ActiveModal } from '@rocketfinancialcorp/rocket-ui/modal';

import { FormModel, GenerateTransactionActivityParams } from '@shared/models';
import { formatDate, systemTimeZone } from '@shared/utils';

const dateRangeExpressions = {
  minDate: NgbDate.from({ day: 1, month: 1, year: new Date().getFullYear() - 5 }),
  maxDate: NgbDate.from({ day: 1, month: 1, year: new Date().getFullYear() + 5 }),
};

@Component({
  selector: 'app-mocketbnk-transaction-activity-generator',
  imports: [RktFormComponent, RktIconComponent, RktButtonDirective, ReactiveFormsModule],
  templateUrl: './mocketbnk-transaction-activity-generator.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class MocketbnkTransactionActivityGeneratorComponent {
  formModel: FormModel<GenerateTransactionActivityParams> = {};

  transactionActivityForm = new FormGroup({});

  submitError = '';

  isLoading = false;

  transactionActivityFormFields: FormlyFieldConfig[] = [
    formRow([
      formInput({
        key: 'count',
        label: 'Number of Transactions',
        props: {
          placeholder: '0',
          required: true,
          pattern: /^\d{1,2}$/,
        },
        validation: {
          messages: {
            pattern: 'Please enter a number between 1 and 99.',
          },
        },
      }),
    ]),
    formRow([
      formDatepicker({
        key: 'startDate',
        label: 'Start Date',
        props: dateRangeExpressions,
        expressions: {
          'props.maxDate': (field: FormlyFieldConfig) => {
            return field.model?.endDate ? formatDate(toZonedTime(new Date(field.model.endDate), systemTimeZone)) : null;
          },
        },
      }),
      formDatepicker({
        key: 'endDate',
        label: 'End Date',
        props: dateRangeExpressions,
        expressions: {
          'props.minDate': (field: FormlyFieldConfig) => {
            return field.model?.startDate ? formatDate(toZonedTime(new Date(field.model.startDate), systemTimeZone)) : null;
          },
        },
      }),
    ]),
    formRow([
      formInput({
        key: 'minAmount',
        label: 'Minimum Transaction Amount',
        props: {
          type: 'number',
        },
        expressions: {
          'props.max': (field: FormlyFieldConfig) => {
            return field.model?.maxAmount ? field.model?.maxAmount : null;
          },
        },
        validation: {
          messages: {
            max: 'Please enter an amount less than the maximum number.',
          },
        },
      }),
      formInput({
        key: 'maxAmount',
        label: 'Maximum Transaction Amount',
        props: {
          type: 'number',
        },
        expressions: {
          'props.min': (field: FormlyFieldConfig) => {
            return field.model?.minAmount ? field.model?.minAmount : null;
          },
        },
        validation: {
          messages: {
            min: 'Please enter an amount greater than the minimum amount.',
          },
        },
      }),
    ]),
  ];

  constructor(public activeModal: ActiveModal) {}

  onFormReset() {
    this.transactionActivityForm.reset(undefined);
  }

  onSave() {
    this.activeModal.close(this.formModel);
  }
}
